import { Dispatch, SetStateAction } from 'react'

import { useEditRemoveSOP } from './useEditRemoveSOP'
import { useLightMode } from '@/hooks/useLightMode'
import { useDispatch } from 'react-redux'

import { StandardOperatingProceduresQuery } from '@/graphql/generated/operations'
import { setSOPPageSize } from '@/redux/ui/uiSlice'

import { useColumns } from '../components/Columns'
import { formatTableData } from '../utils/query'

interface UseSOPsTableIProps {
  sops?: StandardOperatingProceduresQuery
  hiddenColumns?: string[]
  useMonoDate?: boolean
  useCompactLayout?: boolean
  pageIndex: number
  setPageIndex: Dispatch<SetStateAction<number>>
  pageSize: number
  setPageSize: Dispatch<SetStateAction<number>>
  sortColumn: string | null
  setSortColumn: Dispatch<SetStateAction<string | null>>
  sortOrder: 'asc' | 'desc' | null
  setSortOrder: Dispatch<SetStateAction<'asc' | 'desc' | null>>
}

export const useSOPsTable = ({
  sops,
  hiddenColumns = [],
  pageIndex,
  setPageIndex,
  pageSize,
  setPageSize,
  setSortColumn,
  setSortOrder,
  useMonoDate,
  useCompactLayout,
}: UseSOPsTableIProps) => {
  const dispatch = useDispatch()
  const colorScheme = useLightMode()

  const {
    sop,
    isEditSOPOpen,
    isRemoveSOPOpen,
    onEditSOPClose,
    onRemoveSOPClose,
    onSOPEdit,
    onSOPRemove,
  } = useEditRemoveSOP()

  const allColumns = useColumns({ useMonoDate, useCompactLayout })
  const columns =
    hiddenColumns.length > 0
      ? allColumns.filter((col) => !hiddenColumns.includes(col.key))
      : allColumns
  const total = sops?.standardOperatingProcedures?.totalCount ?? 0
  const tableData = formatTableData(sops, onSOPEdit, onSOPRemove)
  const pageCount = Math.ceil(total / pageSize)
  const sortableColumns = ['name', 'date', 'creator']

  const handleSortChange = (
    column: string | null,
    order: 'asc' | 'desc' | null
  ) => {
    setSortColumn(column)
    setSortOrder(order)
    setPageIndex(0)
  }

  const handlePageSizeChange = (size: number) => {
    dispatch(setSOPPageSize(size))
    setPageSize(size)
    setPageIndex(0)
  }

  return {
    colorScheme,
    columns,
    tableData,
    total,
    pageIndex,
    setPageIndex,
    pageSize,
    pageCount,
    sortableColumns,
    handleSortChange,
    handlePageSizeChange,
    sop,
    isEditSOPOpen,
    isRemoveSOPOpen,
    onEditSOPClose,
    onRemoveSOPClose,
  }
}
